/**
 * Which pages Session Replay may record, and how thinly it samples them.
 *
 * The rule lives here, in bundled code, and not only in the loader script in index.html. That
 * script is inline, so a stale hash in the page's CSP stops the browser running it, and with the
 * flags it sets left undefined every page looks like a creator page. That is what happened
 * between 2026-10-13 and 2026-10-20: participant survey pages recorded replays, answers and all
 * (docs/security/DPIA_BACKLOG.md). A privacy guarantee must not rest on an inline script.
 *
 * It also keeps the replay budget in reach. The plan holds 50 replays a month, and 10 % of every
 * session on staging as well as production spent them all by the middle of it — 86 % of them on
 * sessions with no error in them, which nobody opens. So only production is sampled, and thinly;
 * a session that hit an error is still kept wherever recording is allowed, because those are the
 * replays anyone actually watches.
 */

/** 2 % of creator sessions: a handful a month, which leaves the budget to the error replays. */
const PRODUCTION_SESSION_RATE = 0.02;

/** Participant views: the survey host, and /survey, /join and /survey-join anywhere. As index.html. */
const PARTICIPANT_HOST = /^survey(-[a-z]+)?\./;
const PARTICIPANT_PATH = /^\/(survey|join|survey-join)(\/|$)/;
/** The admin dashboard's own site (ceo.durvey.org), as index.html. */
const STAFF_HOST = /^ceo(-[a-z]+)?\./;
/** The admin dashboard on the creator host, where it still lives too (app/hosts.ts). */
const ADMIN_PATH = /^\/admin(\/|$)/;
/** The partner portal: its own site (partner.durvey.org) and /partner elsewhere. It shows customers'
 *  names and payout accounts (docs/plans/PARTNER_PROGRAM_PLAN.md), so it is never recorded. */
const PARTNER_HOST = /^partner(-[a-z]+)?\./;
const PARTNER_PATH = /^\/partner(\/|$)/;
/**
 * Our own walkthroughs, and whatever crawls the Vercel deployment URLs. A declared crawler names
 * itself as `…bot/2.1` or `…bot;`, never as a bare word, so the "bot" part needs what follows it.
 */
const AUTOMATED_AGENT = /Headless|bot[/;)\s]|crawler|spider|Lighthouse|PhantomJS/i;

export function isParticipantView(hostname: string, pathname: string): boolean {
  return PARTICIPANT_HOST.test(hostname) || PARTICIPANT_PATH.test(pathname);
}

/** This page is on the admin dashboard's own site. */
export function isStaffHost(hostname: string): boolean {
  return STAFF_HOST.test(hostname);
}

/** An admin screen: the dashboard's own site, or /admin on any other host. Full of customer data. */
export function isAdminView(hostname: string, pathname: string): boolean {
  return isStaffHost(hostname) || ADMIN_PATH.test(pathname);
}

/** A partner portal screen. */
export function isPartnerView(hostname: string, pathname: string): boolean {
  return PARTNER_HOST.test(hostname) || PARTNER_PATH.test(pathname);
}

export function isAutomated(userAgent: string, webdriver?: boolean): boolean {
  return webdriver === true || AUTOMATED_AGENT.test(userAgent);
}

/** The path of a router URL, which can carry a query string or a fragment. */
export function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

export interface ReplayContext {
  hostname: string;
  pathname: string;
  userAgent: string;
  webdriver?: boolean;
  production: boolean;
}

export interface ReplayRates {
  /** `replaysSessionSampleRate`. */
  session: number;
  /** `replaysOnErrorSampleRate`. */
  onError: number;
}

/** Whether this page may be recorded at all. */
export function replayAllowed(context: ReplayContext): boolean {
  return !isParticipantView(context.hostname, context.pathname)
    && !isAdminView(context.hostname, context.pathname)
    && !isPartnerView(context.hostname, context.pathname)
    && !isAutomated(context.userAgent, context.webdriver);
}

/** The two sample rates for this page. Both 0 means the integration isn't loaded at all. */
export function replayRates(context: ReplayContext): ReplayRates {
  if (!replayAllowed(context)) {
    return { session: 0, onError: 0 };
  }
  return { session: context.production ? PRODUCTION_SESSION_RATE : 0, onError: 1 };
}
